/**
 * Authentication Service
 * Handles API calls for login, token validation, and auth management
 * Implements secure token storage and refresh logic
 */

import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.API_BASE_URL || 'http://localhost:5000/api';
const AUTH_ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/auth/login`,
  VALIDATE_TOKEN: `${API_BASE_URL}/auth/validate-token`,
  REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh-token`,
  LOGOUT: `${API_BASE_URL}/auth/logout`,
  GET_ME: `${API_BASE_URL}/auth/me`,
  CHANGE_PASSWORD: `${API_BASE_URL}/auth/change-password`,
};

class AuthService {
  /**
   * Admin Login
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise} - Login response with token
   */
  static async login(email, password) {
    try {
      const response = await axios.post(AUTH_ENDPOINTS.LOGIN, {
        email: email.trim(),
        password: password.trim()
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Store token securely
        const token = response.data.data.token;
        const user = response.data.data.user || null;
        this.setAuthSession(token, user);
        
        return {
          success: true,
          token,
          user
        };
      }

      // If response indicates failure, throw an error
      const errorMsg = response.data.message || 'Login failed';
      const error = new Error(errorMsg);
      error.code = response.data.code || 'LOGIN_ERROR';
      throw error;

    } catch (error) {
      // Handle axios error responses
      if (error.response?.data?.message) {
        const err = new Error(error.response.data.message);
        err.code = error.response.data.code || 'LOGIN_ERROR';
        throw err;
      }
      
      // Re-throw as Error instance
      if (error instanceof Error) {
        throw error;
      }
      
      // Fallback
      throw new Error(error.message || 'Login failed');
    }
  }

  /**
   * Logout
   * Clears stored token
   */
  static async logout() {
    try {
      const token = this.getToken();
      
      if (token) {
        await axios.post(AUTH_ENDPOINTS.LOGOUT, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      this.clearToken();
      return { success: true };

    } catch (error) {
      // Clear token even if logout fails
      this.clearToken();
      return {
        success: false,
        message: error.message
      };
    }
  }

  /**
   * Set JWT Token
   * Stores token in localStorage with expiry check
   */
  static setToken(token) {
    try {
      if (!token) return false;

      // Store token
      localStorage.setItem('adminToken', token);
      
      // Store timestamp for token refresh logic
      localStorage.setItem('tokenSetTime', Date.now().toString());
      
      // Dispatch custom event to notify App component (same tab)
      window.dispatchEvent(new Event('tokenUpdated'));

      return true;
    } catch (error) {
      console.error('Error storing token:', error);
      return false;
    }
  }

  /**
   * Set auth session (token + user payload)
   */
  static setAuthSession(token, user) {
    const ok = this.setToken(token);
    if (!ok) return false;
    try {
      if (user) {
        localStorage.setItem('adminUser', JSON.stringify(user));
      }
      // Notify app after BOTH token and user are stored
      // (setToken emits once too, but that can happen before adminUser is written).
      window.dispatchEvent(new Event('tokenUpdated'));
      return true;
    } catch (error) {
      console.error('Error storing user session:', error);
      return false;
    }
  }

  /**
   * Get JWT Token
   * Retrieves token from localStorage
   */
  static getToken() {
    try {
      return localStorage.getItem('adminToken');
    } catch (error) {
      console.error('Error retrieving token:', error);
      return null;
    }
  }

  /**
   * Clear Token
   * Removes token from localStorage
   */
  static clearToken() {
    try {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('tokenSetTime');
      localStorage.removeItem('adminUser');
      return true;
    } catch (error) {
      console.error('Error clearing token:', error);
      return false;
    }
  }

  /**
   * Get stored authenticated user payload
   */
  static getUser() {
    try {
      const raw = localStorage.getItem('adminUser');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.error('Error reading stored user:', error);
      return null;
    }
  }

  /**
   * Is Authenticated
   * Checks if valid token exists
   */
  static isAuthenticated() {
    const token = this.getToken();
    return !!token;
  }

  /**
   * Get Authorization Header
   * Returns header object for API requests
   */
  static getAuthHeader() {
    const token = this.getToken();
    if (!token) {
      return {};
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Validate Token
   * Checks if token is valid on server
   */
  static async validateToken() {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          success: false,
          message: 'No token found'
        };
      }

      const response = await axios.post(AUTH_ENDPOINTS.VALIDATE_TOKEN, 
        { token },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;

    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Token validation failed'
      };
    }
  }

  /**
   * Refresh Token
   * Gets new token using valid existing token
   */
  static async refreshToken() {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          success: false,
          message: 'No token to refresh'
        };
      }

      const response = await axios.post(AUTH_ENDPOINTS.REFRESH_TOKEN, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        this.setToken(response.data.data.token);
      }

      return response.data;

    } catch (error) {
      this.clearToken();
      return {
        success: false,
        message: error.response?.data?.message || 'Token refresh failed'
      };
    }
  }

  /**
   * Get Current User
   * Fetches authenticated user info
   */
  static async getCurrentUser() {
    try {
      const response = await axios.get(AUTH_ENDPOINTS.GET_ME, {
        headers: this.getAuthHeader()
      });

      return response.data;

    } catch (error) {
      if (error.response?.status === 401) {
        this.clearToken();
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get user info'
      };
    }
  }

  /**
   * Change Password
   * Updates admin password
   */
  static async changePassword(currentPassword, newPassword, confirmPassword) {
    try {
      const response = await axios.post(AUTH_ENDPOINTS.CHANGE_PASSWORD,
        {
          currentPassword,
          newPassword,
          confirmPassword
        },
        {
          headers: this.getAuthHeader()
        }
      );

      return response.data;

    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to change password'
      };
    }
  }

  /**
   * Setup Axios Interceptor
   * Automatically includes token in all requests
   */
  static setupInterceptor() {
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for handling 401 errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Don't redirect on login endpoint 401 (invalid credentials)
          const isLoginEndpoint = error.config?.url?.includes('/auth/login');
          
          if (!isLoginEndpoint) {
            this.clearToken();
            window.location.href = '/admin/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }
}

// Setup interceptor on module load
AuthService.setupInterceptor();

export default AuthService;
